import { useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'

import {
  AccountIcon,
  Avatar,
  BankIcon,
  CoinsIcon,
  LogoMark,
  LogoutIcon,
  MenuIcon,
  MoonIcon,
  OverviewIcon,
  SunIcon,
  TicketIcon,
  UsersIcon,
} from '@/components/icons'
import { Sheet } from '@/components/ui/Sheet'
import { pct } from '@/lib/format'
import { useTheme, type ThemeChoice } from '@/lib/theme'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/auth'

/**
 * The partner shell, the admin portal's frame: a left sidebar from md up, a
 * bottom tab bar on phones, a slim top bar with who is signed in.
 */
const NAV = [
  { to: '/', label: 'Dashboard', short: 'Home', end: true, icon: OverviewIcon, tab: true },
  { to: '/coupons', label: 'Coupons', short: 'Coupons', end: false, icon: TicketIcon, tab: true },
  { to: '/users', label: 'Users onboarded', short: 'Users', end: false, icon: UsersIcon, tab: true },
  { to: '/earnings', label: 'Earnings', short: 'Earnings', end: false, icon: CoinsIcon, tab: true },
  { to: '/settlements', label: 'Settlements', short: 'Settled', end: false, icon: BankIcon, tab: false },
  { to: '/account', label: 'Account', short: 'Account', end: false, icon: AccountIcon, tab: false },
] as const

export function PartnerLayout() {
  const partner = useAuthStore((state) => state.partner)
  const logout = useAuthStore((state) => state.logout)
  const [, resolved, setTheme] = useTheme()
  const [more, setMore] = useState(false)
  const location = useLocation()
  const inMore = NAV.some((item) => !item.tab && location.pathname.startsWith(item.to))

  return (
    <div className="min-h-dvh bg-canvas md:flex">
      {/* ---- Sidebar (md+) ---- */}
      <aside className="sticky top-0 hidden h-dvh w-[68px] shrink-0 flex-col border-r border-line bg-surface md:flex lg:w-[232px]">
        <Link to="/" className="flex h-14 items-center gap-2.5 px-3.5 lg:px-5" aria-label="JobSlide Partners home">
          <LogoMark />
          <span className="hidden min-w-0 lg:block">
            <span className="block text-[16px] font-semibold leading-none tracking-tight text-ink">JobSlide</span>
            <span className="mt-1 block text-[10.5px] font-semibold uppercase leading-none tracking-[0.12em] text-ink-muted">Partners</span>
          </span>
        </Link>

        <nav aria-label="Main" className="flex flex-1 flex-col gap-0.5 px-2.5 pt-2 lg:px-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              title={item.label}
              className={({ isActive }) =>
                cn(
                  'group relative flex h-10 items-center gap-3 rounded-field px-2.5 text-[14px] font-medium transition-colors',
                  isActive ? 'bg-accent-soft text-ink' : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <span aria-hidden="true" className="absolute -left-2.5 top-2 h-6 w-[3px] rounded-r bg-brand lg:-left-3" />}
                  <item.icon className="h-[18px] w-[18px] shrink-0" />
                  <span className="hidden min-w-0 flex-1 truncate lg:inline">{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {partner && (
          <div className="hidden border-t border-line p-4 lg:block">
            <p className="label-eyebrow">Your maximum</p>
            <p className="num mt-1.5 text-[20px] font-semibold leading-none text-ink">{pct(partner.maxDiscountPct)}</p>
            <p className="mt-1.5 text-[12px] leading-snug text-ink-muted">Split it between the customer's discount and your commission.</p>
          </div>
        )}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* ---- Top bar ---- */}
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2.5 border-b border-line bg-surface/90 px-4 backdrop-blur-md sm:px-6 lg:px-8" style={{ paddingTop: 'var(--safe-top)' }}>
          <Link to="/" className="flex items-center gap-2 md:hidden" aria-label="JobSlide Partners home">
            <LogoMark className="h-7 w-7" />
          </Link>
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <Avatar name={partner?.name} size="sm" />
            <div className="min-w-0 leading-tight">
              <p className="flex min-w-0 items-center gap-1.5">
                <span className="truncate text-[14px] font-semibold text-ink">{partner?.name}</span>
                {partner?.tag && <span className="num shrink-0 rounded-[6px] bg-info/[0.12] px-1.5 py-0.5 text-[11px] font-semibold text-info">{partner.tag}</span>}
              </p>
              {partner?.company && <p className="hidden truncate text-[12px] text-ink-muted sm:block">{partner.company}</p>}
            </div>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => setTheme(resolved === 'dark' ? 'light' : 'dark')}
              aria-label={resolved === 'dark' ? 'Use light mode' : 'Use dark mode'}
              title={resolved === 'dark' ? 'Light mode' : 'Dark mode'}
              className="grid h-9 w-9 place-items-center rounded-field text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink"
            >
              {resolved === 'dark' ? <SunIcon /> : <MoonIcon />}
            </button>
            <button
              type="button"
              onClick={logout}
              className="flex h-9 items-center gap-2 rounded-field px-2.5 text-[13.5px] font-medium text-ink-soft transition-colors hover:bg-surface-muted hover:text-ink"
            >
              <LogoutIcon />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </header>

        <main className="page-pad-bottom mx-auto w-full min-w-0 max-w-[1280px] flex-1 animate-fade-in px-4 pt-5 sm:px-6 sm:pt-6 lg:px-8 2xl:max-w-[1480px]">
          <Outlet />
        </main>
      </div>

      {/* ---- Bottom tab bar (phones) ---- */}
      <nav aria-label="Main" className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 backdrop-blur-md md:hidden" style={{ paddingBottom: 'var(--safe-bottom)' }}>
        <ul className="grid h-[var(--tabbar-h)] grid-cols-5">
          {NAV.filter((item) => item.tab).map((item) => (
            <li key={item.to} className="min-w-0">
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) => cn('relative flex h-full flex-col items-center justify-center gap-1 text-[10.5px] font-medium transition-colors', isActive ? 'text-ink' : 'text-ink-muted')}
              >
                {({ isActive }) => (
                  <>
                    <span className={cn('grid h-7 w-12 place-items-center rounded-pill transition-colors', isActive && 'bg-accent-soft')}>
                      <item.icon className="h-[20px] w-[20px]" />
                    </span>
                    <span className="truncate">{item.short}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
          <li className="min-w-0">
            <button type="button" onClick={() => setMore(true)} className={cn('flex h-full w-full flex-col items-center justify-center gap-1 text-[10.5px] font-medium', inMore ? 'text-ink' : 'text-ink-muted')}>
              <span className={cn('grid h-7 w-12 place-items-center rounded-pill', inMore && 'bg-accent-soft')}>
                <MenuIcon className="h-[20px] w-[20px]" />
              </span>
              More
            </button>
          </li>
        </ul>
      </nav>

      <MoreSheet open={more} onClose={() => setMore(false)} />
    </div>
  )
}

function MoreSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const partner = useAuthStore((state) => state.partner)
  const logout = useAuthStore((state) => state.logout)
  const [choice, , setTheme] = useTheme()
  const options: Array<{ value: ThemeChoice; label: string }> = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'system', label: 'System' },
  ]
  return (
    <Sheet open={open} onClose={onClose} side="bottom" size="sm">
      <div className="flex items-center gap-3 pb-4">
        <Avatar name={partner?.name} size="lg" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold text-ink">{partner?.name}</p>
          <p className="truncate text-[13px] text-ink-muted">{partner?.email}</p>
          <p className="mt-1 inline-flex gap-1.5 text-[11px] font-medium">
            <span className="rounded-pill bg-info/[0.12] px-2 py-0.5 text-info">{partner?.tag}</span>
            <span className="rounded-pill bg-brand-soft px-2 py-0.5 text-brand">Max {pct(partner?.maxDiscountPct)}</span>
          </p>
        </div>
      </div>

      <nav aria-label="More" className="mb-4 grid gap-1">
        {NAV.filter((item) => !item.tab).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onClose}
            className={({ isActive }) => cn('flex h-11 items-center gap-3 rounded-field px-3 text-[14.5px] font-medium', isActive ? 'bg-accent-soft text-ink' : 'text-ink-soft hover:bg-surface-muted')}
          >
            <item.icon className="h-[18px] w-[18px]" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="pb-4">
        <p className="label-eyebrow mb-2">Theme</p>
        <div role="radiogroup" aria-label="Theme" className="grid grid-cols-3 gap-1 rounded-field bg-surface-muted p-1">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={choice === option.value}
              onClick={() => setTheme(option.value)}
              className={cn('h-9 rounded-[8px] text-[13px] font-medium transition-colors', choice === option.value ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink')}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <button type="button" onClick={logout} className="h-11 w-full rounded-field text-[14px] font-medium text-danger transition-colors hover:bg-danger/5">
        Sign out
      </button>
    </Sheet>
  )
}
